import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type WaveCommentRecord,
  type WaveGalleryItem,
  type WaveParticipantRecord,
} from "@vida/wave-core";
import { signedDownloadURL } from "../../services/waveBlobUpload";
import { WaveCommentThread } from "../editor/WaveCommentThread";

export type WaveGalleryViewerProps = {
  waveId: string;
  blockId: string;
  items: WaveGalleryItem[];
  initialIndex: number;
  remoteContentRevision?: number;
  comments: WaveCommentRecord[];
  participants: WaveParticipantRecord[];
  mentionParticipants?: WaveParticipantRecord[];
  currentUserSub: string;
  readOnly?: boolean;
  onClose: () => void;
  onPostComment: (
    body: string,
    parentId: string | null,
    galleryItemId: string,
  ) => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
};

function galleryItemLabel(items: WaveGalleryItem[], itemId: string): string {
  const index = items.findIndex((i) => i.id === itemId);
  const item = index >= 0 ? items[index] : undefined;
  if (item?.fileName?.trim()) return item.fileName.trim();
  if (index >= 0) return `Immagine ${index + 1}`;
  return "Immagine";
}

function GallerySlideImage({
  waveId,
  item,
  remoteContentRevision,
}: {
  waveId: string;
  item: WaveGalleryItem;
  remoteContentRevision: number;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const status = item.uploadStatus ?? "uploading";

  useEffect(() => {
    if (!item.s3Key || status !== "ready") {
      setUrl(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const signed = await signedDownloadURL(item.s3Key!, waveId, true, item.fileName);
        if (!cancelled) setUrl(signed);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.s3Key, item.fileName, status, waveId, remoteContentRevision]);

  if (status !== "ready" || !url) {
    return <span className="wave-gallery-viewer-placeholder muted">Caricamento…</span>;
  }

  return (
    <img
      src={url}
      alt={item.caption ?? item.fileName ?? "Immagine galleria"}
      className="wave-gallery-viewer-image"
      draggable={false}
    />
  );
}

export function WaveGalleryViewer({
  waveId,
  blockId,
  items,
  initialIndex,
  remoteContentRevision = 0,
  comments,
  participants,
  mentionParticipants,
  currentUserSub,
  readOnly = false,
  onClose,
  onPostComment,
  onToggleReaction,
}: WaveGalleryViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1)),
  );
  const [commentsOpen, setCommentsOpen] = useState(true);

  const currentItem = items[index];
  const currentItemId = currentItem?.id ?? "";

  const imageCommentCount = useMemo(
    () =>
      comments.filter(
        (c) =>
          c.blockAnchorId === blockId &&
          c.galleryItemId === currentItemId &&
          !c.parentId,
      ).length,
    [comments, blockId, currentItemId],
  );

  const scrollToIndex = useCallback((next: number) => {
    const el = scrollRef.current;
    if (!el || items.length === 0) return;
    const clamped = Math.min(Math.max(0, next), items.length - 1);
    const slide = el.children[clamped] as HTMLElement | undefined;
    slide?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setIndex(clamped);
  }, [items.length]);

  const goPrev = useCallback(() => scrollToIndex(index - 1), [index, scrollToIndex]);
  const goNext = useCallback(() => scrollToIndex(index + 1), [index, scrollToIndex]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const slide = el.children[initialIndex] as HTMLElement | undefined;
    slide?.scrollIntoView({ inline: "center", block: "nearest" });
    setIndex(Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1)));
  }, [initialIndex, items.length]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goNext, goPrev, onClose]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || items.length < 2) return;
    const onScroll = () => {
      const width = el.clientWidth;
      if (width <= 0) return;
      const center = el.scrollLeft + width / 2;
      const slideIndex = Math.round(center / width);
      const clamped = Math.min(Math.max(0, slideIndex), items.length - 1);
      setIndex((prev) => (prev === clamped ? prev : clamped));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [items.length]);

  if (!currentItem) return null;

  return (
    <div
      className="wave-gallery-viewer-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="wave-gallery-viewer"
        role="dialog"
        aria-modal="true"
        aria-label="Galleria immagini"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wave-gallery-viewer-header">
          <div className="wave-gallery-viewer-title">
            <strong>{currentItem.fileName ?? "Immagine"}</strong>
            {items.length > 1 && (
              <span className="muted">
                {index + 1} / {items.length}
              </span>
            )}
          </div>
          <div className="wave-gallery-viewer-header-actions">
            <button
              type="button"
              className="secondary-btn"
              aria-pressed={commentsOpen}
              onClick={() => setCommentsOpen((v) => !v)}
            >
              Commenti{imageCommentCount > 0 ? ` (${imageCommentCount})` : ""}
            </button>
            <button type="button" className="icon-btn" aria-label="Chiudi" onClick={onClose}>
              ✕
            </button>
          </div>
        </header>

        <div className="wave-gallery-viewer-stage">
          {items.length > 1 && (
            <button
              type="button"
              className="wave-gallery-viewer-nav wave-gallery-viewer-nav--prev"
              aria-label="Immagine precedente"
              disabled={index <= 0}
              onClick={goPrev}
            >
              ‹
            </button>
          )}

          <div ref={scrollRef} className="wave-gallery-viewer-scroll" tabIndex={0}>
            {items.map((item) => (
              <div key={item.id} className="wave-gallery-viewer-slide">
                <GallerySlideImage
                  waveId={waveId}
                  item={item}
                  remoteContentRevision={remoteContentRevision}
                />
              </div>
            ))}
          </div>

          {items.length > 1 && (
            <button
              type="button"
              className="wave-gallery-viewer-nav wave-gallery-viewer-nav--next"
              aria-label="Immagine successiva"
              disabled={index >= items.length - 1}
              onClick={goNext}
            >
              ›
            </button>
          )}
        </div>

        {commentsOpen && (
          <aside className="wave-gallery-viewer-comments">
            <p className="wave-gallery-viewer-comments-hint muted">
              Commento su {galleryItemLabel(items, currentItemId)}
            </p>
            <div className="wave-gallery-viewer-comments-scroll">
              <WaveCommentThread
              comments={comments}
              blockAnchorId={blockId}
              participants={participants}
              mentionParticipants={mentionParticipants ?? participants}
              currentUserSub={currentUserSub}
              readOnly={readOnly}
              galleryItemIdFilter={currentItemId}
              galleryItems={items}
              pinComposeFooter
              onPost={(body, parentId) =>
                onPostComment(body, parentId, currentItemId)
              }
              onToggleReaction={onToggleReaction}
            />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
